import { Base } from "../../framework/base";
import { IEditItemLockable, EditItemLockable } from "../common/editItemLockable";
import { IFileAttachment, FileAttachment } from "../common/fileAttachment";
import { ITaskDataRowCol, TaskDataRowCol, SaveTaskDataRowCol } from "./taskDataRowCol";
import { ITaskDataRowColIcon } from "./taskDataRowColIcon";
import { ITaskEditTypeItem } from "./taskEditTypeItem";
import { ITaskTypeCol, TaskTypeCol, createDateField, createUserNameField, modifyDateField, modifyUserNameField } from "./taskTypeCol";

export interface ITaskDataRow extends IEditItemLockable {
    createDate: number;
    createUserCode: string;
    createUserName: string;
    createUserPicture: string;
    modifyDate: number;
    modifyUserCode: string;
    modifyUserName: string;
    modifyUserPicture: string;
    state: number;
    hasAttachments: boolean;
    cols: ITaskDataRowCol[];
    attachments: IFileAttachment[];

    getHash(): string;
    initializeCols(taskType: ITaskEditTypeItem);
    initializeToTaskType(taskType: ITaskEditTypeItem, dataCols: ITaskDataRowCol[]);
    hasDefaultValues(taskType: ITaskEditTypeItem): boolean;
    matchesToFilter(lowFilter: string, taskType: ITaskEditTypeItem): boolean;
    getTaskDataRowColUiValue(field: string, taskType: ITaskEditTypeItem): string;
    getTaskDataRowColListUiValue(field: string, taskType: ITaskEditTypeItem): { text: string, icon?: ITaskDataRowColIcon };
    getTaskDataRowColSortValue(field: string, taskType: ITaskEditTypeItem): string;
}

export class TaskDataRow extends EditItemLockable implements ITaskDataRow {
    createDate: number;
    createUserCode: string;
    createUserName: string;
    createUserPicture: string;
    modifyDate: number;
    modifyUserCode: string;
    modifyUserName: string;
    modifyUserPicture: string;
    state: number;
    hasAttachments: boolean;
    cols: TaskDataRowCol[];
    attachments: FileAttachment[];

    constructor();
    constructor(obj: ITaskDataRow);
    constructor(obj?: any) {
        super(obj);
        this.createDate = obj && obj.createDate || 0;
        this.createUserCode = obj && obj.createUserCode || "";
        this.createUserName = obj && obj.createUserName || "";
        this.createUserPicture = obj && obj.createUserPicture || "";
        this.modifyDate = obj && obj.modifyDate || 0;
        this.modifyUserCode = obj && obj.modifyUserCode || "";
        this.modifyUserName = obj && obj.modifyUserName || "";
        this.modifyUserPicture = obj && obj.modifyUserPicture || "";
        this.state = obj && obj.state || 0;
        this.hasAttachments = obj && obj.hasAttachments || false;
        this.cols = Base.getTypedArray<TaskDataRowCol>(obj ? obj.cols : null, TaskDataRowCol);
        this.attachments = Base.getTypedArray<FileAttachment>(obj ? obj.attachments : null, FileAttachment);
    }

    initializeCols(taskType: ITaskEditTypeItem) {
        this.cols = TaskDataRowCol.initializeValuesByType(taskType.formCols, this.cols);
    }

    initializeToTaskType(taskType: ITaskEditTypeItem, dataCols: ITaskDataRowCol[] = []) {
        if (Base.isNullOrUndefined(taskType)) return;
        this.cols = TaskDataRowCol.initializeValuesByType(taskType.formCols, dataCols);
    }

    hasDefaultValues(taskType: ITaskEditTypeItem): boolean {
        if (Base.isNullOrUndefined(taskType)) return true;
        return TaskDataRowCol.hasDefaultValues(taskType.formCols, this.cols);
    }

    matchesToFilter(lowFilter: string, taskType: ITaskEditTypeItem): boolean {
        const lineCols = taskType.lineCols.filter(i => i.isSaveDataType() && i.isSavedTaskDataField());
        const formCols = taskType.formCols.filter(i => i.isSaveDataType() && i.isSavedTaskDataField());
        for (const lineCol of lineCols) {
            const formIndex = formCols.findIndex(j => j.field === lineCol.field);
            if (formIndex < 0 || formIndex > this.cols.length - 1) {
                continue;
            }
            const dataIndex = this.cols.findIndex(i => i.field === lineCol.field);
            if (dataIndex < 0 || dataIndex > this.cols.length - 1) {
                continue;
            }
            if (this.cols[dataIndex].matchesToFilter(lowFilter, formCols[formIndex], this.createUserName, this.createDate, this.modifyUserName, this.modifyDate)) {
                return true;
            }
        }
        return false;
    }

    getTaskDataRowColUiValue(field: string, taskType: ITaskEditTypeItem): string {
        const formIndex = taskType.formCols.findIndex(i => i.field === field);
        if (formIndex < 0 || formIndex > this.cols.length - 1) return "";
        const dataIndex = this.cols.findIndex(i => i.field === field);
        if (dataIndex < 0 || dataIndex > this.cols.length - 1) return "";
        const col = this.cols[dataIndex];
        return TaskDataRowCol.getTaskDataRowColUiValue(taskType.formCols[formIndex], col.valueStr, col.valueNbr);
    }

    getTaskDataRowColListUiValue(field: string, taskType: ITaskEditTypeItem): { text: string, icon?: ITaskDataRowColIcon } {
        if (field.equalIgnoreCase(createUserNameField)) {
            return {
                text: this.createUserName
            };
        }
        if (field.equalIgnoreCase(createDateField)) {
            return {
                text: Base.timeToDateTimeStr(this.createDate)
            };
        }
        if (field.equalIgnoreCase(modifyUserNameField)) {
            return {
                text: this.modifyUserName
            };
        }
        if (field.equalIgnoreCase(modifyDateField)) {
            return {
                text: Base.timeToDateTimeStr(this.modifyDate)
            };
        }
        const formIndex = taskType.formCols.findIndex(i => i.field === field);
        if (formIndex < 0 || formIndex > taskType.formCols.length - 1) return { text: "" };
        const dataIndex = this.cols.findIndex(i => i.field === field);
        if (dataIndex < 0 || dataIndex > this.cols.length - 1) return { text: "" };
        return {
            text: this.cols[dataIndex].getTaskDataRowColListUiValue(taskType.formCols[formIndex], this.createUserName, this.createDate, this.modifyUserName, this.modifyDate),
            icon: this.cols[dataIndex].icon
        };
    }

    getTaskDataRowColSortValue(field: string, taskType: ITaskEditTypeItem): string {
        if (field.equalIgnoreCase(createUserNameField)) {
            return this.createUserName;
        }
        if (field.equalIgnoreCase(createDateField)) {
            return Base.padZeros((this.createDate ?? 0).toString(10), 20);
        }
        if (field.equalIgnoreCase(modifyUserNameField)) {
            return this.modifyUserName;
        }
        if (field.equalIgnoreCase(modifyDateField)) {
            return Base.padZeros((this.modifyDate ?? 0).toString(10), 20);
        }
        const typeCol = taskType.formCols.find(i => i.field === field);
        if (!typeCol) return "";
        const dataCol = this.cols.find(i => i.field === field);
        if (!dataCol) return "";
        return TaskDataRowCol.getTaskDataRowColSortValue(typeCol, dataCol.valueStr, dataCol.valueNbr);
    }

    getHash(): string {
        let result = super.getHash();
        for (const col of this.cols) {
            result = result + "##" + col.getHash();
        }
        return result;
    }

    static getHash(items: ITaskDataRow[]): string {
        if (!items || items.length < 1) return "";
        return Base.joinSuffix(items.map(i => i.getHash()), "#");
    }

    static sortTaskDataRows(rows: ITaskDataRow[], taskType: ITaskEditTypeItem) {
        if (Base.isNullOrUndefined(rows) || rows.length < 2 || !taskType || taskType.lineCols.length < 1) return;
        //Normal form cols and implicit task type cols
        const formCols = taskType.formCols.filter(i => i.isSaveDataType() && i.isSavedTaskDataField()).concat(TaskTypeCol.getImplicitJsonTaskTypeCols());
        const createUserNameIndex = formCols.findIndex(i => i.field === createUserNameField);
        const createDateIndex = formCols.findIndex(i => i.field === createDateField);
        const modifyUserNameIndex = formCols.findIndex(i => i.field === modifyUserNameField);
        const modifyDateIndex = formCols.findIndex(i => i.field === modifyDateField);
        //Line cols
        const lineCols = taskType.lineCols.filter(i => i.isSaveDataType());
        if (!lineCols.find(i => i.field === modifyDateField)) { // ensure that all rows have unique sort value by adding modify date as sort field
            lineCols.push(formCols.find(i => i.field === modifyDateField));
        }
        if (!lineCols.find(i => i.field === createDateField)) { // ensure that all rows have unique sort value by adding create date as sort field
            lineCols.push(formCols.find(i => i.field === createDateField));
        }
        const dataColIndexes: number[] = [];
        for (let i = 0; i < lineCols.length; i++) {
            dataColIndexes[i] = formCols.findIndex(j => j.field.equalIgnoreCase(lineCols[i].field));
        }
        const sortFunction: (a: ITaskDataRow, b: ITaskDataRow) => number =
            (a: ITaskDataRow, b: ITaskDataRow) => {
                for (let i = 0; i < lineCols.length; i++) {
                    if (!lineCols[i].defaultSortField) continue;
                    let result = 0;
                    const formColIndex = dataColIndexes[i];
                    if (formColIndex === createUserNameIndex) {
                        result = TaskDataRowCol.strCompare(a.createUserName, b.createUserName, lineCols[i].defaultSortOrderAsc);
                    } else if (formColIndex === createDateIndex) {
                        result = TaskDataRowCol.numCompare(a.createDate, b.createDate, lineCols[i].defaultSortOrderAsc);
                    } else if (formColIndex === modifyUserNameIndex) {
                        result = TaskDataRowCol.strCompare(a.modifyUserName, b.modifyUserName, lineCols[i].defaultSortOrderAsc);
                    } else if (formColIndex === modifyDateIndex) {
                        result = TaskDataRowCol.numCompare(a.modifyDate, b.modifyDate, lineCols[i].defaultSortOrderAsc);
                    } else {
                        if (formColIndex < 0 || a.cols.length <= formColIndex || b.cols.length <= formColIndex) continue;
                        result = TaskDataRowCol.compareTaskDataRowCols(a.cols[formColIndex], b.cols[formColIndex], formCols[formColIndex], lineCols[i].defaultSortOrderAsc);
                    }
                    if (result !== 0) return result;
                }
                return 0;
            };
        rows.sort(sortFunction);
    }
 
    static sortTaskDataRowsByColumn(rows: ITaskDataRow[], taskType: ITaskEditTypeItem, column: string, asc: boolean) {
        if (Base.isNullOrUndefined(rows) || rows.length < 2 || !taskType || taskType.lineCols.length < 1) return;
        const lineCols = taskType.lineCols.filter(i => i.isSaveDataType() && i.isSavedTaskDataField());
        const formCols = taskType.formCols.filter(i => i.isSaveDataType() && i.isSavedTaskDataField());
        const dataColIndexes: number[] = [];
        for (let i = 0; i < lineCols.length; i++) {
            dataColIndexes[i] = formCols.findIndex(j => j.field === lineCols[i].field);
        }
        const sortFunction: (a: ITaskDataRow, b: ITaskDataRow) => number =
            (a: ITaskDataRow, b: ITaskDataRow) => {
                for (let i = 0; i < lineCols.length; i++) {
                    const formColIndex = dataColIndexes[i];
                    if (formColIndex < 0 || a.cols.length <= formColIndex || b.cols.length <= formColIndex || lineCols[i].field !== column) continue;
                    const result = TaskDataRowCol.compareTaskDataRowCols(a.cols[formColIndex], b.cols[formColIndex], formCols[formColIndex], asc);
                    if (result !== 0) return result;
                }
                return 0;
            };
        rows.sort(sortFunction);
    }
    
    static createTaskDataRow(cols: ITaskDataRowCol[], attachments: IFileAttachment[]): TaskDataRow {
        const result = new TaskDataRow();
        result.id = Base.getGuid();
        result.cols = cols.slice(0);
        result.attachments = attachments.slice(0);
        return result;
    }

    static getDynamicDataRowsFromJsonTaskDataRows(rows: ITaskDataRow[], typeCols: ITaskTypeCol[]): any[] {
        const result: any[] = [];
        for (const row of rows) {
            const resultRow: any = {};
            for (const col of row.cols) {
                const typeCol = typeCols.find(i => i.field === col.field);
                if (!typeCol) continue;
                resultRow[typeCol.field] = TaskDataRowCol.getTaskDataRowColDbValue(typeCol, col.valueStr, col.valueNbr);
            }
            result.push(resultRow);
        }
        return result;
    }

    static getNewRowDefaultValuesFromTaskDataRow(taskType: ITaskEditTypeItem, previousRow: ITaskDataRow, firstRowCols: ITaskDataRowCol[]): ITaskDataRowCol[] {
        const result: ITaskDataRowCol[] = [];
        const initialiseFromRows: { targetField: string, sourceField: string, sourceRow: number }[] = [];
        for (const col of taskType.formCols) {
            if (!col.defaultExpression) continue;
            const values = col.defaultExpression.split("#");
            if (values.length > 2) {
                if (values[0].equalIgnoreCase("row")) {
                    initialiseFromRows.push({
                        targetField: col.field,
                        sourceField: values[2],
                        sourceRow: values[1].toInteger()
                    });
                }
            }
        }
        if (!initialiseFromRows.length) return result;
        // console.log("initialiseFromRows", initialiseFromRows);
        // console.log("previousRow.cols", previousRow.cols);
        // console.log("firstRowCols", firstRowCols);
        for (const initialiseFromRow of initialiseFromRows) {
            // ASSUME ALWAYS LAST ROW (previousRow)
            // var dataRow = initialiseFromRow.SourceRow == 0
            //     ? list.FirstOrDefault()
            //     : list.LastOrDefault();
            // if (dataRow == null) continue;
            const dataCol = initialiseFromRow.sourceRow === 0
                ? previousRow.cols.find(i => i.field.equalIgnoreCase(initialiseFromRow.sourceField))
                : firstRowCols.find(i => i.field.equalIgnoreCase(initialiseFromRow.targetField));
            if (dataCol == null) continue;
            const taskDataRowCol = new TaskDataRowCol();
            taskDataRowCol.field = initialiseFromRow.targetField;
            taskDataRowCol.valueStr = dataCol.valueStr;
            taskDataRowCol.valueNbr = dataCol.valueNbr;
            result.push(taskDataRowCol);
        }
        return result;
    }

    static getImplicitTaskDataRowCols(dataRow: ITaskDataRow): ITaskDataRowCol[] {
        return [
            TaskDataRowCol.crateTaskDataRowCol(createUserNameField, dataRow.createUserName, 0),
            TaskDataRowCol.crateTaskDataRowCol(createDateField, Base.timeToDateTimeStr(dataRow.createDate), dataRow.createDate),
            TaskDataRowCol.crateTaskDataRowCol(modifyUserNameField, dataRow.modifyUserName, 0),
            TaskDataRowCol.crateTaskDataRowCol(modifyDateField, Base.timeToDateTimeStr(dataRow.modifyDate), dataRow.modifyDate)
        ];
    }
}

export class SaveTaskDataRow {
    id: string;
    rowId: string;
    cols: SaveTaskDataRowCol[];

    static createSaveTaskDataRow(formCols: ITaskTypeCol[], taskDataRow: ITaskDataRow, cols: ITaskDataRowCol[]): SaveTaskDataRow {
        const result = new SaveTaskDataRow();
        result.id = taskDataRow.id || "";
        result.rowId = taskDataRow.rowId || "";
        result.cols = [];
        const savedCols = TaskDataRowCol.getToBeSavedTaskDataRowCols(formCols, cols || taskDataRow.cols || []);
        for (const col of savedCols) {
            result.cols.push(new SaveTaskDataRowCol(col));
        }
        return result;
    }
}
